import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WarningIcon from "@material-ui/icons/Warning";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  modelsForProvider,
  defaultModelFor,
  providerSupports,
} from "../../helpers/aiModels";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 760 },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  field: { marginBottom: theme.spacing(2) },
  help: { color: theme.palette.text.secondary, fontSize: 12, marginBottom: theme.spacing(1) },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
  providerRow: { marginBottom: theme.spacing(1) },
  actions: { marginTop: theme.spacing(2) },
  warnBox: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    background: theme.palette.type === "dark" ? "#3a2e12" : "#fffbeb",
    borderLeft: "4px solid #f59e0b",
    padding: "8px 12px",
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  warnText: { fontSize: 12, color: theme.palette.type === "dark" ? "#f6d58a" : "#92400e" },
}));

const PROVIDERS = [
  { key: "openai", label: "OpenAI" },
  { key: "anthropic", label: "Anthropic" },
  { key: "gemini", label: "Gemini" },
];

// Catálogo de servicios renderizados. Sumar uno = una línea acá (+ i18n).
// modality gobierna el filtro de proveedores y de modelos.
const SERVICES = [
  { key: "transcription", modality: "transcribe", hasMode: true },
];

const MODES = ["auto", "a_pedido", "nunca"];

const AIServices = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [providers, setProviders] = useState({});
  const [services, setServices] = useState({});

  const [keyEdit, setKeyEdit] = useState({});
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState({});
  const [savingKey, setSavingKey] = useState({});
  const [savingService, setSavingService] = useState({});

  const t = (k, opts) => i18n.t(`aiServices.${k}`, opts);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/admin/config");
      setEnabled(!!data.enabled);
      setProviders(data.providers || {});
      // normaliza defaults de servicio para la UI
      const svc = { ...(data.services || {}) };
      SERVICES.forEach((s) => {
        const cur = svc[s.key] || {};
        const provider =
          cur.provider && providerSupports(cur.provider, s.modality)
            ? cur.provider
            : "openai";
        svc[s.key] = {
          enabled: cur.enabled === true,
          provider,
          model: cur.model || defaultModelFor(provider, s.modality),
          mode: cur.mode || "a_pedido",
          // feat_150 — defaults que preservan el comportamiento histórico.
          aiAttendedMode: cur.aiAttendedMode === "nunca" ? "nunca" : "siempre",
          maxPerWindow:
            typeof cur.maxPerWindow === "number" ? cur.maxPerWindow : 0,
          windowMinutes:
            typeof cur.windowMinutes === "number" ? cur.windowMinutes : 60,
        };
      });
      setServices(svc);
      setKeyEdit({});
      setRevealed({});
      setRevealing({});
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const toggleReveal = async (which) => {
    if (revealing[which]) {
      setRevealing((r) => ({ ...r, [which]: false }));
      setRevealed((v) => ({ ...v, [which]: null }));
      return;
    }
    try {
      const { data } = await api.get("/ai/admin/reveal-key", { params: { which } });
      setRevealed((v) => ({ ...v, [which]: data.key || "" }));
      setRevealing((r) => ({ ...r, [which]: true }));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveKey = async (provider) => {
    setSavingKey((s) => ({ ...s, [provider]: true }));
    try {
      const payload = { provider };
      if ((keyEdit[provider] || "").trim()) payload.key = keyEdit[provider].trim();
      const { data } = await api.put("/ai/admin/keys", payload);
      setProviders((p) => ({
        ...p,
        [provider]: { keySet: !!data.keySet, enabled: !!data.enabled },
      }));
      setKeyEdit((k) => ({ ...k, [provider]: "" }));
      setRevealed((v) => ({ ...v, [provider]: null }));
      setRevealing((r) => ({ ...r, [provider]: false }));
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSavingKey((s) => ({ ...s, [provider]: false }));
  };

  const handleSaveService = async (s) => {
    const cur = services[s.key] || {};
    setSavingService((x) => ({ ...x, [s.key]: true }));
    try {
      const { data } = await api.put("/ai/admin/services", {
        serviceKey: s.key,
        enabled: cur.enabled === true,
        provider: cur.provider,
        model: cur.model,
        ...(s.hasMode ? { mode: cur.mode } : {}),
        ...(s.modality === "transcribe"
          ? {
              aiAttendedMode: cur.aiAttendedMode,
              maxPerWindow: cur.maxPerWindow,
              windowMinutes: cur.windowMinutes,
            }
          : {}),
      });
      setServices((sv) => ({ ...sv, [s.key]: { ...cur, ...(data.service || {}) } }));
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSavingService((x) => ({ ...x, [s.key]: false }));
  };

  const keyPlaceholder = (isSet) => (isSet ? t("keyConfigured") : t("keyNotSet"));

  const renderKeyField = (provider, label) => {
    const p = providers[provider] || {};
    const isRevealing = revealing[provider];
    return (
      <div key={provider} className={classes.providerRow}>
        <TextField
          label={label}
          type={isRevealing ? "text" : "password"}
          variant="outlined"
          fullWidth
          size="small"
          className={classes.field}
          value={isRevealing ? (revealed[provider] || "") : (keyEdit[provider] || "")}
          onChange={(e) =>
            !isRevealing && setKeyEdit((k) => ({ ...k, [provider]: e.target.value }))
          }
          placeholder={keyPlaceholder(p.keySet)}
          autoComplete="new-password"
          InputProps={{
            readOnly: isRevealing,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  title={isRevealing ? t("hideKey") : t("showKey")}
                  onClick={() => toggleReveal(provider)}
                  disabled={!p.keySet}
                >
                  {isRevealing ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={savingKey[provider] ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          disabled={savingKey[provider]}
          onClick={() => handleSaveKey(provider)}
        >
          {t("save")}
        </Button>
      </div>
    );
  };

  const updateService = (key, patch) =>
    setServices((sv) => ({ ...sv, [key]: { ...(sv[key] || {}), ...patch } }));

  const renderService = (s) => {
    const cur = services[s.key] || {};
    return (
      <Paper key={s.key} className={classes.paper}>
        <FormControlLabel
          control={
            <Switch
              checked={cur.enabled === true}
              onChange={(e) => updateService(s.key, { enabled: e.target.checked })}
              color="primary"
            />
          }
          label={t(`${s.key}.title`)}
        />
        <Typography className={classes.help}>{t(`${s.key}.help`)}</Typography>

        <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
          <InputLabel>{t("provider")}</InputLabel>
          <Select
            value={cur.provider || "openai"}
            onChange={(e) => {
              const provider = e.target.value;
              const models = modelsForProvider(provider, s.modality);
              const model = models.includes(cur.model) ? cur.model : defaultModelFor(provider, s.modality);
              updateService(s.key, { provider, model });
            }}
            label={t("provider")}
          >
            {/* MUI Select necesita MenuItem como hijos directos (no envolver en
                Tooltip/span: rompe el mapeo de value). El proveedor sin la
                modalidad se deshabilita con sufijo explicativo. */}
            {PROVIDERS.map(({ key, label }) => {
              const ok = providerSupports(key, s.modality);
              return (
                <MenuItem key={key} value={key} disabled={!ok}>
                  {label}{!ok ? ` — ${t("notSupported")}` : ""}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
          <InputLabel>{t("model")}</InputLabel>
          <Select
            value={cur.model || ""}
            onChange={(e) => updateService(s.key, { model: e.target.value })}
            label={t("model")}
          >
            {/* valor guardado fuera del catálogo → mostrarlo igual, no perderlo */}
            {cur.model && !modelsForProvider(cur.provider, s.modality).includes(cur.model) && (
              <MenuItem value={cur.model}>{cur.model}</MenuItem>
            )}
            {modelsForProvider(cur.provider, s.modality).map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {s.hasMode && (
          <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
            <InputLabel>{t("modeHuman")}</InputLabel>
            <Select
              value={cur.mode || "a_pedido"}
              onChange={(e) => updateService(s.key, { mode: e.target.value })}
              label={t("modeHuman")}
            >
              {MODES.map((m) => (
                <MenuItem key={m} value={m}>{t(`modes.${m}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* feat_150 — controles exclusivos de transcripción. */}
        {s.modality === "transcribe" && (
          <>
            <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
              <InputLabel>{t("aiAttendedMode")}</InputLabel>
              <Select
                value={cur.aiAttendedMode || "siempre"}
                onChange={(e) => updateService(s.key, { aiAttendedMode: e.target.value })}
                label={t("aiAttendedMode")}
              >
                <MenuItem value="siempre">{t("aiAttendedSiempre")}</MenuItem>
                <MenuItem value="nunca">{t("aiAttendedNunca")}</MenuItem>
              </Select>
            </FormControl>
            <Typography className={classes.help}>{t("aiAttendedHelp")}</Typography>

            <Typography className={classes.sectionTitle} style={{ marginTop: 8 }}>
              {t("limitSection")}
            </Typography>
            <Typography className={classes.help}>{t("limitHelp")}</Typography>
            <TextField
              type="number"
              label={t("maxPerWindow")}
              variant="outlined"
              fullWidth
              size="small"
              className={classes.field}
              value={cur.maxPerWindow ?? 0}
              onChange={(e) => updateService(s.key, { maxPerWindow: e.target.value })}
              inputProps={{ min: 0, max: 1000 }}
            />
            <TextField
              type="number"
              label={t("windowMinutes")}
              variant="outlined"
              fullWidth
              size="small"
              className={classes.field}
              value={cur.windowMinutes ?? 60}
              onChange={(e) => updateService(s.key, { windowMinutes: e.target.value })}
              inputProps={{ min: 1, max: 1440 }}
            />
          </>
        )}

        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={savingService[s.key] ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={savingService[s.key]}
            onClick={() => handleSaveService(s)}
          >
            {t("save")}
          </Button>
        </div>
      </Paper>
    );
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className={classes.root}>
        <Typography variant="h5">{t("title")}</Typography>
        <Typography className={classes.help} style={{ marginTop: 16 }}>
          {t("notEnabled")}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h5">{t("title")}</Typography>

      <div className={classes.warnBox}>
        <WarningIcon style={{ color: "#f59e0b", fontSize: 18 }} />
        <span className={classes.warnText}>{t("consumptionWarning")}</span>
      </div>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>{t("keysSection")}</Typography>
        <Typography className={classes.help}>{t("keysHelp")}</Typography>
        {PROVIDERS.map(({ key, label }) =>
          renderKeyField(key, t("apiKey", { provider: label }))
        )}
      </Paper>

      <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
        {t("servicesSection")}
      </Typography>
      {SERVICES.map(renderService)}
    </div>
  );
};

export default AIServices;
